import {Floorplan, FloorplanHotspot} from "../../../api/apiClient";
import { ReactComponent as Cone} from "../../../images/ui/cone.svg"
import "../../../animations/fadeIn.css"
import {alpha, Box, IconButton, SvgIconProps, useMediaQuery, useTheme} from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import {Vector3} from "three";
import {useEffect, useRef, useState} from "react";

export interface BrowserFloorMapProps {
    changePanorama: (panorama: string, zoomTowards?: Vector3, continueTour?: boolean) => void;
    floorPlan: Floorplan;
    currentPanorama: string;
    onFloorPlanImageLoaded: () => void;
    showViewpoints: boolean;
}

// On-screen hotspot size, whatever size the floorplan image is rendered at. The hit area stays bigger
// than the marker it draws, so small markers are still easy to hit on a phone.
const HOTSPOT_HIT_AREA_PX = 24;
const HOTSPOT_HIT_AREA_LARGE_SCREEN_PX = 28;
const HOTSPOT_MARKER_PX = 12;

// The dot marking where the camera stands, drawn over the tip of the view cone.
const CAMERA_DOT_SIZE_PX = 12;
const CAMERA_DOT_BORDER_PX = 2;

/** Width of the view cone as a share of the plan, so it keeps its proportions at any render size. */
const CONE_WIDTH_PERCENT = 20;

// Outline of view cone to support visibility with different background colors.
const CONE_FILL_OPACITY = 0.35;
const CONE_OUTLINE_WIDTH_PX = 1.5;
const CONE_OUTLINE_OPACITY = 0.7;

// rotationReader writes the cone's rotation as the camera turns, so this only smooths between updates.
const CONE_ROTATION_TRANSITION = "transform 0.1s linear";

/** Tint behind the viewpoint the user is standing on, and behind whichever one the pointer is over. */
const HOTSPOT_HIGHLIGHT_OPACITY = 0.4;

const PLAN_CORNER_RADIUS_PX = 8;

// Painting order within the plan: the cone spreads out from under the viewpoint markers, and the camera
// dot sits on top of both.
const CONE_Z_INDEX = 1;
const HOTSPOT_Z_INDEX = 2;
const CAMERA_DOT_Z_INDEX = 3;

interface Size {
    width: number;
    height: number;
}

const EMPTY_SIZE: Size = {width: 0, height: 0};

function sameSize(a: Size, b: Size) {
    return a.width === b.width && a.height === b.height;
}

/** A point on the plan, as a percentage of the rendered image's width and height. */
interface PlanPoint {
    topPercent: number;
    leftPercent: number;
}

/** A filled circle with a ring inside it, so the marker stays legible over a busy plan. */
function ViewpointMarkerIcon(props: SvgIconProps) {
    return (
        <SvgIcon {...props}>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
            >
                <circle cx="12" cy="12" r="10" fill="currentColor"/>
                <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="2"/>
            </svg>
        </SvgIcon>
    );
}

/**
 * Where a hotspot sits on the plan. Hotspots arrive as pixels in the source image, which only mean
 * anything as a share of it: the plan is rendered at whatever size its container allows.
 */
function hotSpotCenter(hotSpot: FloorplanHotspot, floorPlan: Floorplan): PlanPoint {
    return {
        topPercent: (hotSpot.y / floorPlan.imageHeight) * 100,
        leftPercent: (hotSpot.x / floorPlan.imageWidth) * 100
    };
}

export function BrowserFloorMap(props: BrowserFloorMapProps) {
    const {changePanorama, floorPlan, currentPanorama, onFloorPlanImageLoaded, showViewpoints} = props;

    const theme = useTheme();
    const isVeryLargeScreen = useMediaQuery(theme.breakpoints.up("xl"));

    const containerRef = useRef<HTMLDivElement | null>(null);

    // Measured rather than derived from the window, because the floorplan lives in a panel or sheet.
    const [containerSize, setContainerSize] = useState<Size>(EMPTY_SIZE);

    useEffect(() => {
        const container = containerRef.current;

        if (!container) {
            return;
        }

        const measure = () => {
            const size = {width: container.clientWidth, height: container.clientHeight};

            // Only replace state on a real change, or the observer would keep waking itself.
            setContainerSize(current => sameSize(current, size) ? current : size);
        };

        const observer = new ResizeObserver(measure);
        observer.observe(container);

        return () => observer.disconnect();
    }, []);

    // Sized here rather than by max-width and max-height, which only ever shrink: a plan smaller than
    // its box would stop short of the gutter the other rows start from.
    const renderScale = Math.min(containerSize.width / floorPlan.imageWidth,
        containerSize.height / floorPlan.imageHeight);

    const renderedSize: Size = renderScale > 0 && Number.isFinite(renderScale)
        ? {width: floorPlan.imageWidth * renderScale, height: floorPlan.imageHeight * renderScale}
        : EMPTY_SIZE;

    const isPlanMeasured = renderedSize.width > 0 && renderedSize.height > 0;

    const hitAreaPx = isVeryLargeScreen ? HOTSPOT_HIT_AREA_LARGE_SCREEN_PX : HOTSPOT_HIT_AREA_PX;

    // Everything on the plan is positioned in percentages, so the hit area has to be expressed in them
    // too. It is a square on screen, which is not a square share of a plan that is not itself square.
    const hitAreaPercent = {
        width: isPlanMeasured ? (hitAreaPx / renderedSize.width) * 100 : 0,
        height: isPlanMeasured ? (hitAreaPx / renderedSize.height) * 100 : 0
    };

    const currentHotSpot = floorPlan.hotspots.find(hotSpot => hotSpot.targetPanorama === currentPanorama);

    // Where the user is standing: the anchor both the cone and the camera dot hang from.
    const cameraPosition = currentHotSpot && isPlanMeasured
        ? hotSpotCenter(currentHotSpot, floorPlan)
        : undefined;

    const highlightBackground = alpha(theme.palette.background.default, HOTSPOT_HIGHLIGHT_OPACITY);

    return <div ref={containerRef}
                style={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden"
                }}>

        {/*Shrink-wraps the image, so hotspot percentages are relative to the image and not to a larger
           box it is letterboxed inside.*/}
        <div style={{position: "relative", display: "inline-block", lineHeight: 0}}>

            {/*The floorplan image*/}
            <img
                src={floorPlan.image}
                alt={floorPlan.imageHeader}
                style={{
                    display: "block",
                    width: renderedSize.width || "auto",
                    height: renderedSize.height || "auto",
                    borderRadius: PLAN_CORNER_RADIUS_PX,
                    // Until the container has been measured, at least keep the image inside it.
                    maxWidth: "100%",
                    maxHeight: "100%"
                }}
                onLoad={onFloorPlanImageLoaded}
            />

            {/*Viewpoint markers. Positioned by their top-left corner, so each one is pulled back by half
               its hit area to leave its middle on the hotspot.*/}
            {isPlanMeasured && showViewpoints && floorPlan.hotspots.map((hotSpot) => {

                const center = hotSpotCenter(hotSpot, floorPlan);
                const isCurrent = hotSpot.targetPanorama === currentHotSpot?.targetPanorama;

                const label = isCurrent
                    ? `${hotSpot.targetPanorama}, current viewpoint`
                    : `Go to ${hotSpot.targetPanorama}`;

                return <IconButton key={"browser-minimap-button-" + hotSpot.targetPanorama}
                                   aria-label={label}
                                   aria-current={isCurrent ? "location" : undefined}
                                   sx={{
                                       position: "absolute",
                                       zIndex: HOTSPOT_Z_INDEX,
                                       top: (center.topPercent - (hitAreaPercent.height / 2)) + "%",
                                       left: (center.leftPercent - (hitAreaPercent.width / 2)) + "%",
                                       width: hitAreaPercent.width + "%",
                                       // A percentage height would be a share of the plan rather than of
                                       // the button, so the aspect ratio squares it off instead.
                                       height: "auto",
                                       aspectRatio: "1/1",
                                       padding: 0,
                                       color: theme.palette.primary.main,
                                       backgroundColor: isCurrent ? highlightBackground : "",
                                       "& .MuiSvgIcon-root": {
                                           width: HOTSPOT_MARKER_PX,
                                           height: HOTSPOT_MARKER_PX,
                                           fontSize: "inherit",
                                           stroke: theme.palette.primary.contrastText
                                       },
                                       ":hover": {
                                           backgroundColor: highlightBackground
                                       }
                                   }}

                                   onClick={() => {
                                       changePanorama(hotSpot.targetPanorama);
                                   }}>
                    <ViewpointMarkerIcon fontSize={"inherit"}/>
                </IconButton>
            })}

            {/*The view cone, hanging from the camera position and pivoting about it. rotationReader
               rotates this by id and common.ts hides it across panorama changes, so the id has to stay
               as it is. Not tied to showViewpoints: it shows where the user is, not where they could go.*/}
            {cameraPosition && <div id={"cone"} className={"fade-in"}
                                    style={{
                                        top: cameraPosition.topPercent + "%",
                                        // Pulled back by half its own width, which centres it without a
                                        // transform of its own: the transform belongs to rotationReader.
                                        left: (cameraPosition.leftPercent - (CONE_WIDTH_PERCENT / 2)) + "%",
                                        width: CONE_WIDTH_PERCENT + "%",
                                        margin: 0,
                                        padding: 0,
                                        position: "absolute",
                                        zIndex: CONE_Z_INDEX,
                                        transition: CONE_ROTATION_TRANSITION,
                                        transformOrigin: "top center"
                                    }}>
                <Cone style={{
                    fill: theme.palette.primary.main,
                    fillOpacity: CONE_FILL_OPACITY,
                    stroke: theme.palette.common.white,
                    strokeWidth: CONE_OUTLINE_WIDTH_PX,
                    strokeOpacity: CONE_OUTLINE_OPACITY,
                    margin: 0,
                    padding: 0
                }}/>
            </div>}

            {/*Where the camera is standing. Shown with the cone, not with the viewpoint markers.*/}
            {cameraPosition && <Box className={"fade-in"}
                                    sx={{
                                        position: "absolute",
                                        zIndex: CAMERA_DOT_Z_INDEX,
                                        pointerEvents: "none",
                                        top: cameraPosition.topPercent + "%",
                                        left: cameraPosition.leftPercent + "%",
                                        // Centred on the camera position by its own size, as a dot sized
                                        // in pixels has no percentage to be pulled back by.
                                        transform: "translate(-50%, -50%)",
                                        width: CAMERA_DOT_SIZE_PX,
                                        height: CAMERA_DOT_SIZE_PX,
                                        borderRadius: "50%",
                                        backgroundColor: "primary.main",
                                        border: CAMERA_DOT_BORDER_PX,
                                        borderColor: "background.paper"
                                    }}/>}
        </div>
    </div>
}
